import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import type { Client } from '@sirajoptic/shared';
import { Button } from '@/components/ui/button';
import { Combobox, type OptionCombobox } from '@/components/ui/combobox';
import { useAntiRebond } from '@/lib/anti-rebond';
import { telephone } from '@/lib/format';
import { useAutocompleteClients } from '../api/clients.api';
import { NouveauClientDialog } from './NouveauClientDialog';

/**
 * Saisie du client avec suggestions — règle 12.
 *
 * La saisie libre reste possible : taper un nom inconnu crée le client à
 * l'enregistrement. Mais attendre l'enregistrement laisse l'opérateur dans le
 * doute — d'où « Créer la fiche… », qui ouvre le formulaire complet (téléphone,
 * ICE, note) et rattache aussitôt la saisie en cours à un `client_id`. Créer la
 * fiche depuis ici évite d'aller la remplir ailleurs, puis de revenir. C'est ce
 * rattachement qui
 * garantit que l'historique reste sur la même personne d'une visite à l'autre,
 * même si le nom est écrit différemment la fois suivante.
 */
export function ClientAutocomplete({
  nom,
  onChangeNom,
  onSelectionner,
  erreur,
  label = 'Nom & Prénom',
  requis,
  className,
  tel,
  aide = 'Un nom inconnu crée automatiquement la fiche client.',
}: {
  nom: string;
  onChangeNom: (nom: string) => void;
  /** null quand l'utilisateur repart en saisie libre. */
  onSelectionner: (client: Client | null) => void;
  erreur?: string;
  label?: string;
  requis?: boolean;
  className?: string;
  /** Téléphone déjà saisi à côté : repris dans la fiche créée. */
  tel?: string;
  aide?: string;
}) {
  const [terme, setTerme] = useState(nom);
  const [dialogue, setDialogue] = useState(false);

  /* Une requête par lettre balayait les six mille fiches à chaque frappe :
   * saisir « MOHAMED BENNANI » en coûtait quatorze. On n'interroge qu'une fois
   * la frappe posée — `saisi` reste immédiat, lui, car il ne sert qu'ici. */
  const termeDiffere = useAntiRebond(terme);
  const { data, isFetching } = useAutocompleteClients(termeDiffere);

  const resultats = data ?? [];

  const options: OptionCombobox<Client>[] = resultats.map((c) => ({
    cle: c.id,
    libelle: c.nom,
    description: c.tel ? telephone(c.tel) : 'Sans téléphone',
    valeur: c,
  }));

  const saisi = terme.trim();

  /* Le bouton n'apparaît que s'il y a vraiment quelque chose à créer : deux
   * lettres au moins, et aucune fiche portant déjà exactement ce nom — sans
   * quoi on inviterait à fabriquer un doublon. */
  const dejaExistant = resultats.some((c) => c.nom.toLowerCase() === saisi.toLowerCase());
  const peutCreer = saisi.length >= 2 && !dejaExistant;

  return (
    <>
      <Combobox<Client>
        label={label}
        requis={requis}
        erreur={erreur}
        className={className}
        valeur={nom}
        chargement={isFetching || terme !== termeDiffere}
        options={options}
        videMessage={
          saisi.length < 2 ? 'Tapez au moins deux lettres pour chercher' : 'Aucun client existant'
        }
        onChangeTexte={(texte) => {
          setTerme(texte);
          onChangeNom(texte);
          // Repartir en saisie libre détache la commande du client choisi.
          onSelectionner(null);
        }}
        onSelectionner={(option) => {
          setTerme(option.valeur.nom);
          onChangeNom(option.valeur.nom);
          onSelectionner(option.valeur);
        }}
        pied={
          peutCreer ? (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              icone={<UserPlus aria-hidden className="h-3.5 w-3.5" />}
              onClick={() => setDialogue(true)}
            >
              Créer la fiche « {saisi} »…
            </Button>
          ) : null
        }
        aide={aide}
      />

      {/* Ouvert depuis la liste : le nom déjà tapé et le numéro saisi à côté sont
          repris, et la fiche créée est aussitôt rattachée à la saisie en cours. */}
      <NouveauClientDialog
        ouvert={dialogue}
        onFermer={() => setDialogue(false)}
        nomInitial={saisi}
        telInitial={tel ?? ''}
        onCree={(client) => {
          setTerme(client.nom);
          onChangeNom(client.nom);
          onSelectionner(client);
        }}
      />
    </>
  );
}
